import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs,
  deleteDoc,
  runTransaction
} from 'firebase/firestore';
import { auth, db, functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { signInAnonymously, signInWithCustomToken } from 'firebase/auth';
import { ClassMember } from '../types/alarm';
import { StudentCoinService } from './studentCoinService';
import { ItemCardService } from './itemCardService';
import { withMemberGroup } from '../utils/memberGroupPaths';

// Firestore 컬렉션 이름들
const COLLECTIONS = {
  USED_CLASS_CODES: 'usedClassCodes',
  USERS: 'users',
  CLASSES: 'classes',
  MEMBERS_POINT: 'ClassMembersPoint'
};

// 클래스 코드 매핑 정보
interface ClassCodeMapping {
  classCode?: string;         // 8자리 숫자
  userId: string;             // 소유자 ID
  classId: string;            // 실제 클래스 ID
  className?: string;         // 클래스 이름
  createdAt?: Date;
  isActive?: boolean;
}

// 멤버 정보 요약
interface StudentSummary {
  basicInfo: ClassMember;
  totalPoints: number;
  assignmentPoints: number;
  positivePoints: number;
  behaviorPoints: number;
  recentActivity: string[];
  profileImage?: string;
}

// 멤버 접근 결과
interface StudentAccessResult {
  success: boolean;
  student?: StudentSummary;
  /** validateStudentAccess(defer) 시 서버에서 받은 멤버 원본 — getStudentSummary 전용 */
  member?: ClassMember;
  /** true면 멤버 페이지에서 getStudentSummary로 student를 채워야 함 */
  pendingStudentSummary?: boolean;
  classInfo?: {
    className: string;
    classCode: string;
    userId: string;
    classId: string;
    memberGroup?: string; // 학생이 속한 멤버 그룹(선택)
    /** 코디가 설정한 레벨업 기준 포인트 (멤버 접근 시 서버에서 전달) */
    levelUpPoints?: number;
    /** 그룹 키(A,B,…) → 표시 이름 (멤버 접근 시 서버에서 전달) */
    memberGroupNames?: { [groupKey: string]: string };
  };
  error?: string;
  isGuestMode?: boolean; // 게스트 모드 여부
  hasAccessCode?: boolean; // 개별 접근 코드 존재 여부
}

// 클래스 코드 → 위치 캐시 (불필요한 반복 조회/로그 감소)
// null도 캐시하여 존재하지 않는 코드 재조회 비용을 줄인다.
const classLocationCache = new Map<string, { userId: string; classId: string } | null>();

export const classCodeService = {
  /** 멤버 정보 확인(checkStudentInfo) 호출 전에만 사용. 멤버 번호/이름 열거 방지를 위해 인증 필요. */
  async ensureAnonymousAuth(): Promise<void> {
    if (!auth.currentUser) {
      await signInAnonymously(auth);
    }
  },

  // 8자리 숫자 클래스 코드 생성
  generateClassCode(): string {
    return Math.floor(10000000 + Math.random() * 90000000).toString();
  },

  // 클래스 코드 중복 확인
  async isClassCodeAvailable(classCode: string): Promise<boolean> {
    try {
      const codeRef = doc(db, COLLECTIONS.USED_CLASS_CODES, classCode);
      const codeDoc = await getDoc(codeRef);
      return !codeDoc.exists();
    } catch (error) {
      console.error('클래스 코드 중복 확인 실패:', error);
      return false;
    }
  },

  // 사용 가능한 클래스 코드 생성 (하이브리드 방식 - 메타 문서 1회 읽기)
  async generateAvailableClassCode(): Promise<string> {
    try {
      console.log('🔍 사용 가능한 클래스 코드 생성 시작...');
      
      // 메타 문서 참조
      const registryRef = doc(db, COLLECTIONS.USED_CLASS_CODES, 'classCodesRegistry');
      
      // 트랜잭션으로 동시성 문제 해결
      return await runTransaction(db, async (transaction) => {
        // 1. 메타 문서에서 사용된 코드 배열 읽기 (1회 읽기)
        const registryDoc = await transaction.get(registryRef);
        
        let usedCodesSet: Set<string>;
        
        if (!registryDoc.exists()) {
          // 메타 문서가 없으면 새로 생성
          usedCodesSet = new Set<string>();
          console.log('📝 메타 문서가 없어 새로 생성합니다.');
        } else {
          const data = registryDoc.data();
          const usedCodes = data.usedCodes || [];
          usedCodesSet = new Set(usedCodes);
          console.log(`📊 사용 중인 클래스 코드 수: ${usedCodesSet.size}`);
        }
        
        // 2. 사용 가능한 코드 범위 (10000000 ~ 99999999)
        const minCode = 10000000;
        const maxCode = 99999999;
        const totalAvailable = maxCode - minCode + 1;
        
        if (usedCodesSet.size >= totalAvailable) {
          throw new Error('사용 가능한 클래스 코드가 없습니다.');
        }
        
        // 3. 랜덤으로 사용 가능한 코드 찾기 (메모리에서 즉시 확인)
        let attempts = 0;
        const maxAttempts = 1000; // 충분한 시도 횟수
        let newCode: string | null = null;
        
        while (attempts < maxAttempts) {
          const randomCode = Math.floor(minCode + Math.random() * (maxCode - minCode + 1)).toString();
          
          // Set에서 즉시 확인 (네트워크 호출 없음)
          if (!usedCodesSet.has(randomCode)) {
            newCode = randomCode;
            break;
          }
          
          attempts++;
        }
        
        // 4. 랜덤 생성으로 찾지 못한 경우 순차적으로 찾기
        if (!newCode) {
          console.log('🔄 랜덤 생성 실패, 순차 검색 시작...');
          for (let code = minCode; code <= maxCode; code++) {
            const codeStr = code.toString();
            if (!usedCodesSet.has(codeStr)) {
              newCode = codeStr;
              break;
            }
          }
        }
        
        if (!newCode) {
          throw new Error('사용 가능한 클래스 코드를 찾을 수 없습니다.');
        }
        
        // 5. 메타 문서에 새 코드 추가 (트랜잭션 내에서)
        usedCodesSet.add(newCode);
        transaction.set(registryRef, {
          usedCodes: Array.from(usedCodesSet),
          lastUpdated: new Date()
        }, { merge: true });
        
        console.log(`✅ 사용 가능한 클래스 코드 생성 완료: ${newCode} (시도 횟수: ${attempts + 1})`);
        return newCode;
      });
      
    } catch (error) {
      console.error('❌ 사용 가능한 클래스 코드 생성 실패:', error);
      throw error;
    }
  },

  // 클래스 코드 등록 (클래스 생성/수정 시)
  async registerClassCode(
    classCode: string, 
    userId: string, 
    classId: string, 
    className: string
  ): Promise<void> {
    try {
      console.log('🔗 클래스 코드 등록 시작:', { classCode, userId, classId, className });
      
      // 1. 개별 문서 생성 (멤버 로그인용 - O(1) 조회)
      const codeRef = doc(db, COLLECTIONS.USED_CLASS_CODES, classCode);
      
      await setDoc(codeRef, {
        classCode,
        userId,
        classId,
        className,
        createdAt: new Date(),
        isActive: true
      });
      
      // 2. 메타 문서 업데이트 (배열에 추가) - 트랜잭션으로 동시성 문제 해결
      const registryRef = doc(db, COLLECTIONS.USED_CLASS_CODES, 'classCodesRegistry');
      
      await runTransaction(db, async (transaction) => {
        const registryDoc = await transaction.get(registryRef);
        const usedCodes = registryDoc.exists() 
          ? (registryDoc.data().usedCodes || [])
          : [];
        
        // 중복 체크 후 추가
        if (!usedCodes.includes(classCode)) {
          usedCodes.push(classCode);
          transaction.set(registryRef, {
            usedCodes,
            lastUpdated: new Date()
          }, { merge: true });
          console.log('✅ 메타 문서에 클래스 코드 추가 완료');
        } else {
          console.log('ℹ️ 메타 문서에 이미 존재하는 코드입니다.');
        }
      });
      
      console.log('✅ 클래스 코드 등록 완료:', { classCode });
    } catch (error) {
      console.error('❌ 클래스 코드 등록 실패:', error);
      throw error;
    }
  },

  /** 개별 입장: 클래스코드 확인 후 표시용 메타(인증 불필요 Callable). `classEntryCode`가 있으면 DB 값과 일치할 때만 성공. */
  async getClassEntryMeta(
    classCode: string,
    classEntryCode?: string
  ): Promise<{
    success: boolean;
    className?: string;
    memberGroups?: string[];
    memberGroupNames?: { [k: string]: string };
    memberGroupOrder?: string[];
    error?: string;
  }> {
    try {
      const callable = httpsCallable(functions, 'getClassEntryMeta');
      const payload: Record<string, string> = { classCode: String(classCode).trim() };
      const entry = classEntryCode != null ? String(classEntryCode).trim() : '';
      const entryFourDigit = /^\d{4}$/.test(entry);
      if (entry) {
        payload.classEntryCode = entry;
      }
      const result = await callable(payload);
      const data = result.data as {
        success?: boolean;
        className?: string;
        memberGroups?: string[];
        memberGroupNames?: { [k: string]: string };
        memberGroupOrder?: string[];
        error?: string;
        entryCodeChecked?: boolean;
      };
      if (!data?.success) {
        return {
          success: false,
          error: data?.error ?? '클래스 정보를 불러올 수 없습니다.'
        };
      }
      if (entryFourDigit && data.entryCodeChecked !== true) {
        return {
          success: false,
          error:
            '입장 코드 검증이 서버에서 적용되지 않았습니다. Cloud Functions(getClassEntryMeta)를 배포한 뒤 다시 시도해 주세요.'
        };
      }
      return {
        success: true,
        className: data.className,
        memberGroups: data.memberGroups,
        memberGroupNames: data.memberGroupNames,
        memberGroupOrder: data.memberGroupOrder
      };
    } catch (error) {
      console.error('❌ getClassEntryMeta 실패:', error);
      return { success: false, error: '클래스 정보를 불러올 수 없습니다.' };
    }
  },

  // 클래스 코드, 번호, 이름으로 멤버 정보 확인 (개별 접근 코드 확인 전)
  async checkStudentInfo(
    classCode: string,
    studentNumber: string,
    studentName: string,
    memberGroup?: string
  ): Promise<{ found: boolean; hasAccessCode: boolean; studentId?: string }> {
    try {
      await this.ensureAnonymousAuth();
      const callable = httpsCallable(functions, 'checkStudentInfo');
      const payload: Record<string, unknown> = {
        classCode,
        studentNumber,
        studentName
      };
      const g = memberGroup != null ? String(memberGroup).trim().toUpperCase() : '';
      if (g) {
        payload.memberGroup = g;
      }
      const result = await callable(payload);

      const data = result.data as {
        found: boolean;
        hasAccessCode: boolean;
        studentId?: string;
      };

      return {
        found: !!data?.found,
        hasAccessCode: !!data?.hasAccessCode,
        studentId: data?.studentId
      };
    } catch (error) {
      console.error('❌ 멤버 정보 확인 실패:', error);
      return { found: false, hasAccessCode: false };
    }
  },

  // 클래스 코드로 멤버 정보 검증 및 종합 정보 반환 (Custom Token + memberId 매핑으로 동일 uid 재사용)
  async validateStudentAccess(
    classCode: string,
    studentNumber: string,
    studentName: string,
    accessCode?: string, // optional로 변경
    classEntryCode?: string,
    memberGroup?: string,
    options?: { deferStudentSummary?: boolean }
  ): Promise<StudentAccessResult> {
    try {
      const tokenCallable = httpsCallable(functions, 'getOrCreateStudentToken');
      const tokenPayload: Record<string, unknown> = {
        classCode,
        studentNumber,
        studentName,
        accessCode,
        classEntryCode
      };
      const mg = memberGroup != null ? String(memberGroup).trim().toUpperCase() : '';
      if (mg) {
        tokenPayload.memberGroup = mg;
      }
      const tokenResult = await tokenCallable(tokenPayload);
      const tokenData = tokenResult.data as { success?: boolean; token?: string; error?: string; isGuestMode?: boolean; classInfo?: StudentAccessResult['classInfo']; hasAccessCode?: boolean };
      if (!tokenData?.success || tokenData.error) {
        return { success: false, error: tokenData?.error ?? '시스템 오류가 발생했습니다.', hasAccessCode: tokenData?.hasAccessCode };
      }
      if (tokenData.isGuestMode) {
        if (tokenData.token) {
          await signInWithCustomToken(auth, tokenData.token);
        }
        return {
          success: true,
          isGuestMode: true,
          hasAccessCode: tokenData.hasAccessCode,
          classInfo: tokenData.classInfo
        } as StudentAccessResult;
      }
      if (!tokenData.token) {
        return { success: false, error: '시스템 오류가 발생했습니다.' };
      }
      await signInWithCustomToken(auth, tokenData.token);
      const callable = httpsCallable(functions, 'validateStudentAccess');
      const validatePayload: Record<string, unknown> = {
        classCode,
        studentNumber,
        studentName,
        accessCode,
        classEntryCode
      };
      if (mg) {
        validatePayload.memberGroup = mg;
      }
      const result = await callable(validatePayload);

      const data = result.data as (StudentAccessResult & { member?: ClassMember });
      if (!data?.success) {
        return data || { success: false, error: '시스템 오류가 발생했습니다.' };
      }

      if (data.isGuestMode || !data.member || !data.classInfo) {
        return data;
      }

      if (options?.deferStudentSummary) {
        return {
          ...data,
          pendingStudentSummary: true,
          member: data.member
        };
      }

      const studentSummary = await this.getStudentSummary(
        data.classInfo.userId,
        data.classInfo.classId,
        data.member,
        data.classInfo.memberGroup
      );

      return {
        ...data,
        student: studentSummary
      };
    } catch (error) {
      console.error('❌ 멤버 접근 검증 실패:', error);
      return { success: false, error: '시스템 오류가 발생했습니다.' };
    }
  },

  // 멤버의 종합 정보 수집 (다양한 문서에서)
  async getStudentSummary(
    userId: string, 
    classId: string, 
    student: ClassMember,
    memberGroup?: string
  ): Promise<StudentSummary> {
    try {
      console.log('📊 멤버 종합 정보 수집 시작:', { studentId: student.id });

      // 1. 코인 히스토리 로드 (ClassMembersCoin 또는 ClassMembers에서)
      let coinsHistory = student.coinsHistory || [];
      try {
        const studentCoinService = new StudentCoinService(userId, classId, memberGroup);
        const coinHistory = await studentCoinService.getStudentCoinHistory(student.id);
        if (coinHistory && coinHistory.length > 0) {
          coinsHistory = coinHistory.map((history: any) => ({
          ...history,
          earnedAt: history.earnedAt?.toDate ? history.earnedAt.toDate() : 
                   history.earnedAt instanceof Date ? history.earnedAt : 
                   new Date(history.earnedAt || Date.now())
          }));
        } else if (student.coinsHistory && student.coinsHistory.length > 0) {
          // ClassMembers에 있는 경우 사용
          coinsHistory = student.coinsHistory.map((history: any) => ({
            ...history,
            earnedAt: history.earnedAt?.toDate ? history.earnedAt.toDate() : 
                     history.earnedAt instanceof Date ? history.earnedAt : 
                     new Date(history.earnedAt || Date.now())
          }));
        }
      } catch (coinError) {
        console.warn('⚠️ 코인 히스토리 로드 실패, ClassMembers 데이터 사용:', coinError);
        // ClassMembers에 있는 경우 사용
        if (student.coinsHistory && student.coinsHistory.length > 0) {
          coinsHistory = student.coinsHistory.map((history: any) => ({
            ...history,
            earnedAt: history.earnedAt?.toDate ? history.earnedAt.toDate() : 
                     history.earnedAt instanceof Date ? history.earnedAt : 
                     new Date(history.earnedAt || Date.now())
          }));
        }
      }

      // 2. 아이템 카드 로드 (ItemCardService에서)
      let itemCards = student.itemCards || [];
      try {
        const itemCardService = new ItemCardService(userId, classId, memberGroup);
        const studentItemCards = await itemCardService.getStudentItemCards(student.id);
        if (studentItemCards && studentItemCards.length > 0) {
          itemCards = studentItemCards.map((item: any) => ({
            ...item,
            acquiredAt: item.acquiredAt?.toDate ? item.acquiredAt.toDate() : 
                       item.acquiredAt instanceof Date ? item.acquiredAt : 
                       new Date(item.acquiredAt || Date.now())
          }));
        } else if (student.itemCards && student.itemCards.length > 0) {
          // ClassMembers에 있는 경우 사용
          itemCards = student.itemCards.map((item: any) => ({
            ...item,
            acquiredAt: item.acquiredAt?.toDate ? item.acquiredAt.toDate() : 
                       item.acquiredAt instanceof Date ? item.acquiredAt : 
                       new Date(item.acquiredAt || Date.now())
          }));
        }
      } catch (itemCardError) {
        console.warn('⚠️ 아이템 카드 로드 실패, ClassMembers 데이터 사용:', itemCardError);
        // ClassMembers에 있는 경우 사용
        if (student.itemCards && student.itemCards.length > 0) {
          itemCards = student.itemCards.map((item: any) => ({
            ...item,
            acquiredAt: item.acquiredAt?.toDate ? item.acquiredAt.toDate() : 
                       item.acquiredAt instanceof Date ? item.acquiredAt : 
                       new Date(item.acquiredAt || Date.now())
          }));
        }
      }

      // 3. 기본 정보 구성
      const basicInfo: ClassMember = {
        ...student,
        profileImage: student.profileImage || 'basic-0',
        coinsHistory,
        itemCards
      };

      // 2. 포인트 정보 수집 (MembersPoint-X 문서들에서)
      const pointSummary = await this.getStudentPoints(userId, classId, student.id, memberGroup);

      // 3. 최근 활동 정보 수집 (과제 제출, 포인트 획득 등)
      const recentActivity = await this.getRecentActivity(userId, classId, student.id, memberGroup);

      // totalPoints는 행동체크 포인트를 제외한 과제 포인트 + 긍정행동 포인트만 합산
      const displayTotalPoints = pointSummary.assignmentPoints + pointSummary.positivePoints;

      const summary: StudentSummary = {
        basicInfo,
        totalPoints: displayTotalPoints, // 행동체크 포인트 제외
        assignmentPoints: pointSummary.assignmentPoints,
        positivePoints: pointSummary.positivePoints,
        behaviorPoints: pointSummary.behaviorPoints,
        recentActivity,
        profileImage: student.profileImage || 'basic-0'
      };

      console.log('✅ 멤버 종합 정보 수집 완료:', { 
        studentId: student.id, 
        totalPoints: summary.totalPoints 
      });

      return summary;

    } catch (error) {
      console.error('❌ 멤버 종합 정보 수집 실패:', error);
      // 에러가 발생해도 기본 정보는 반환 (coinsHistory의 earnedAt을 Date로 변환)
      // totalPoints는 행동체크 포인트를 제외한 과제 포인트 + 긍정행동 포인트만 합산
      const displayTotalPoints = 0; // 에러 시 0

      // 에러 발생 시에도 코인 히스토리와 아이템 카드 로드 시도
      let coinsHistory = student.coinsHistory || [];
      let itemCards = student.itemCards || [];
      
      try {
        const studentCoinService = new StudentCoinService(userId, classId, memberGroup);
        const coinHistory = await studentCoinService.getStudentCoinHistory(student.id);
        if (coinHistory && coinHistory.length > 0) {
          coinsHistory = coinHistory.map((history: any) => ({
            ...history,
            earnedAt: history.earnedAt?.toDate ? history.earnedAt.toDate() : 
                     history.earnedAt instanceof Date ? history.earnedAt : 
                     new Date(history.earnedAt || Date.now())
          }));
        } else if (student.coinsHistory && student.coinsHistory.length > 0) {
          coinsHistory = student.coinsHistory.map((history: any) => ({
            ...history,
            earnedAt: history.earnedAt?.toDate ? history.earnedAt.toDate() : 
                     history.earnedAt instanceof Date ? history.earnedAt : 
                     new Date(history.earnedAt || Date.now())
          }));
        }
      } catch (e) {
        console.warn('⚠️ 에러 처리 중 코인 히스토리 로드 실패:', e);
      }

      try {
        const itemCardService = new ItemCardService(userId, classId, memberGroup);
        const studentItemCards = await itemCardService.getStudentItemCards(student.id);
        if (studentItemCards && studentItemCards.length > 0) {
          itemCards = studentItemCards.map((item: any) => ({
            ...item,
            acquiredAt: item.acquiredAt?.toDate ? item.acquiredAt.toDate() : 
                       item.acquiredAt instanceof Date ? item.acquiredAt : 
                       new Date(item.acquiredAt || Date.now())
          }));
        } else if (student.itemCards && student.itemCards.length > 0) {
          itemCards = student.itemCards.map((item: any) => ({
            ...item,
            acquiredAt: item.acquiredAt?.toDate ? item.acquiredAt.toDate() : 
                       item.acquiredAt instanceof Date ? item.acquiredAt : 
                       new Date(item.acquiredAt || Date.now())
          }));
        }
      } catch (e) {
        console.warn('⚠️ 에러 처리 중 아이템 카드 로드 실패:', e);
      }

      return {
        basicInfo: {
          ...student,
          coinsHistory,
          itemCards
        },
        totalPoints: displayTotalPoints, // 행동체크 포인트 제외
        assignmentPoints: 0,
        positivePoints: 0,
        behaviorPoints: 0,
        recentActivity: [],
        profileImage: student.profileImage || 'basic-0'
      };
    }
  },

  // 멤버 포인트 정보 수집
  async getStudentPoints(
    userId: string,
    classId: string,
    studentId: string,
    memberGroup?: string
  ): Promise<{
    totalPoints: number;
    assignmentPoints: number;
    positivePoints: number;
    behaviorPoints: number;
  }> {
    try {
      let totalPoints = 0;
      let assignmentPoints = 0;
      let positivePoints = 0;
      let behaviorPoints = 0;

      // V2 듀얼 읽기: students (배열 | 객체) → 배열
      const studentsRawToArray = (raw: any): { studentId: string; points?: any }[] => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw.map((s: any) => ({ studentId: s.studentId ?? s.id, points: s.points }));
        if (typeof raw === 'object') return Object.entries(raw).map(([sid, s]: [string, any]) => ({ studentId: s?.studentId ?? sid, points: s?.points }));
        return [];
      };
      const pointsRawToArray = (raw: any): any[] => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        if (typeof raw === 'object') return Object.values(raw);
        return [];
      };

      // 1. MembersPoint 컬렉션에서 포인트 수집 (그룹별 컬렉션 지원)
      const membersPointCollectionName = withMemberGroup(COLLECTIONS.MEMBERS_POINT, memberGroup);
      const membersPointCollection = collection(
        db, 
        COLLECTIONS.USERS, 
        userId, 
        COLLECTIONS.CLASSES, 
        classId, 
        membersPointCollectionName
      );
      
      const membersPointSnapshot = await getDocs(membersPointCollection);
      
      console.log('🔍 MembersPoint 문서 개수:', membersPointSnapshot.docs.length);

      membersPointSnapshot.docs.forEach(docSnapshot => {
        const data = docSnapshot.data();
        const students = studentsRawToArray(data.students);
        
        console.log('🔍 MembersPoint 문서:', {
          docId: docSnapshot.id,
          studentsCount: students.length,
          hasTargetStudent: students.some((s: any) => s.studentId === studentId)
        });
        
        students.forEach((studentData: any) => {
          if (studentData.studentId === studentId) {
            const pointRecords = pointsRawToArray(studentData.points);
            console.log('🔍 멤버 데이터 전체 구조:', {
              studentId,
              studentData: studentData,
              keys: Object.keys(studentData),
              hasPoints: 'points' in studentData,
              pointsType: typeof studentData.points,
              isArray: Array.isArray(studentData.points)
            });
            
            console.log('🔍 멤버 포인트 레코드:', {
              studentId,
              recordsCount: pointRecords.length,
              records: pointRecords
            });
            
            pointRecords.forEach((record: any) => {
              const points = record.pointValue || 0;
              const category = record.pointCategory as string | undefined;

              // pointCategory 우선 (내역·PointsQueryService와 동일), 레거시는 pointType 폴백
              if (category === 'positive') {
                positivePoints += points;
              } else if (category === 'negative') {
                behaviorPoints += points;
              } else if (record.pointType === '긍정행동' || record.pointType === '일반') {
                positivePoints += points;
              } else if (record.pointType === '행동체크') {
                behaviorPoints += points;
              }
            });
          }
        });
      });

      // 2. 일반 과제 포인트 수집 (generalAssignmentStatuses)
      const { GeneralAssignmentConsolidatedService } = await import('./generalAssignmentConsolidatedService');
      const generalService = new GeneralAssignmentConsolidatedService(userId, classId);
      const generalAssignments = await generalService.loadAllGeneralAssignments();
      
      generalAssignments.forEach(assignment => {
        assignment.studentStatuses.forEach(status => {
          if (status.studentId === studentId) {
            assignmentPoints += status.points || 0;
          }
        });
      });

      // 3. 프로젝트 과제 포인트 수집 (projectAssignmentStatuses)
      const { ProjectAssignmentConsolidatedService } = await import('./projectAssignmentConsolidatedService');
      const projectService = new ProjectAssignmentConsolidatedService(userId, classId);
      const projectAssignments = await projectService.loadAllProjectAssignments();
      
      projectAssignments.forEach(project => {
        project.subTasks.forEach(subTask => {
          subTask.studentStatuses.forEach(status => {
            if (status.studentId === studentId) {
              assignmentPoints += status.earnedPoints || 0;
            }
          });
        });
      });

      // 4. 루틴 과제 포인트 수집 (routineAssignmentStatuses)
      try {
        const { routineAssignmentService } = await import('./routineAssignmentService');
        const { RoutineAssignmentConsolidatedService } = await import('./routineAssignmentConsolidatedService');
        
        const routineAssignments = await routineAssignmentService.loadRoutineAssignments(userId, classId);
        
        for (const routine of routineAssignments) {
          const routineService = new RoutineAssignmentConsolidatedService(userId, classId);
          const routineData = await routineService.loadRoutineAssignmentWithStatuses(routine.id);
          
          for (const dateStatus of routineData.dateStatuses) {
            if (!dateStatus.studentStatuses) continue;
            for (const status of dateStatus.studentStatuses) {
              if (status.studentId === studentId) {
                assignmentPoints += status.points || 0;
                console.log(`✅ 루틴 과제 포인트 추가: ${status.points}점 (과제: ${routine.title})`);
              }
            }
          }
        }
      } catch (error) {
        console.error('⚠️ 루틴 과제 포인트 수집 실패 (무시하고 계속):', error);
      }

      totalPoints = assignmentPoints + positivePoints + behaviorPoints;

      console.log('✅ 멤버 포인트 계산 완료:', {
        studentId,
        totalPoints,
        assignmentPoints,
        positivePoints,
        behaviorPoints
      });

      return {
        totalPoints,
        assignmentPoints,
        positivePoints,
        behaviorPoints
      };

    } catch (error) {
      console.error('❌ 멤버 포인트 정보 수집 실패:', error);
      return {
        totalPoints: 0,
        assignmentPoints: 0,
        positivePoints: 0,
        behaviorPoints: 0
      };
    }
  },

  // 최근 활동 정보 수집
  async getRecentActivity(
    userId: string,
    classId: string,
    studentId: string,
    memberGroup?: string
  ): Promise<string[]> {
    try {
      // MembersPoint 컬렉션의 모든 문서를 가져옴
      const membersPointCollectionName = withMemberGroup(COLLECTIONS.MEMBERS_POINT, memberGroup);
      const membersPointCollection = collection(
        db, 
        COLLECTIONS.USERS, 
        userId, 
        COLLECTIONS.CLASSES, 
        classId, 
        membersPointCollectionName
      );
      
      const snapshot = await getDocs(membersPointCollection);
      
      const allActivities: { date: Date; text: string }[] = [];

      const studentsRawToArrayForActivity = (raw: any): { studentId: string; pointRecords?: any; points?: any }[] => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw.map((s: any) => ({ studentId: s.studentId ?? s.id, pointRecords: s.pointRecords, points: s.points }));
        if (typeof raw === 'object') return Object.entries(raw).map(([sid, s]: [string, any]) => ({ studentId: s?.studentId ?? sid, pointRecords: s?.pointRecords, points: s?.points }));
        return [];
      };
      const pointsRawToArrayForActivity = (raw: any): any[] => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        if (typeof raw === 'object') return Object.values(raw);
        return [];
      };

      snapshot.docs.forEach(docSnapshot => {
        const data = docSnapshot.data();
        const students = studentsRawToArrayForActivity(data.students);
        
        students.forEach((studentData: any) => {
          if (studentData.studentId === studentId) {
            const pointRecords = pointsRawToArrayForActivity(studentData.pointRecords ?? studentData.points);
            
            pointRecords.forEach((record: any) => {
              let date = new Date();
              
              if (record.recordedDate) {
                if (record.recordedDate.toDate) {
                  date = record.recordedDate.toDate();
                } else if (record.recordedDate instanceof Date) {
                  date = record.recordedDate;
                } else if (typeof record.recordedDate === 'string') {
                  date = new Date(record.recordedDate);
                }
              }
              
              const dateStr = date.toLocaleDateString('ko-KR');
              const memo = record.pointMemo || '포인트 획득';
              const points = record.pointValue || 0;
              
              allActivities.push({
                date,
                text: `${dateStr}: ${memo} (${points > 0 ? '+' : ''}${points}점)`
              });
            });
          }
        });
      });

      // 날짜순 정렬 (최신순)
      allActivities.sort((a, b) => b.date.getTime() - a.date.getTime());
      
      // 최근 5개만 반환
      return allActivities.slice(0, 5).map(activity => activity.text);

    } catch (error) {
      console.error('❌ 최근 활동 정보 수집 실패:', error);
      return [];
    }
  },

  // 클래스 코드 해제 (클래스 삭제 시)
  async unregisterClassCode(classCode: string): Promise<void> {
    try {
      console.log('🔓 클래스 코드 해제 시작:', { classCode });
      
      // 1. 개별 문서 삭제
      const codeRef = doc(db, COLLECTIONS.USED_CLASS_CODES, classCode);
      await deleteDoc(codeRef); // 완전 삭제
      
      // 2. 메타 문서에서도 제거 - 트랜잭션으로 동시성 문제 해결
      const registryRef = doc(db, COLLECTIONS.USED_CLASS_CODES, 'classCodesRegistry');
      
      await runTransaction(db, async (transaction) => {
        const registryDoc = await transaction.get(registryRef);
        
        if (registryDoc.exists()) {
          const data = registryDoc.data();
          const usedCodes = (data.usedCodes || []).filter((code: string) => code !== classCode);
          
          transaction.set(registryRef, {
            usedCodes,
            lastUpdated: new Date()
          }, { merge: true });
          console.log('✅ 메타 문서에서 클래스 코드 제거 완료');
        }
      });
      
      console.log('✅ 클래스 코드 해제 완료:', { classCode });
    } catch (error) {
      console.error('❌ 클래스 코드 해제 실패:', error);
      throw error;
    }
  },

  /**
   * 모든 usedClassCodes를 검증하고 유효하지 않은 매핑 제거
   * - 실제 클래스가 존재하지 않는 클래스 코드 제거
   * - 사용자가 존재하지 않는 클래스 코드 제거
   */
  async cleanupOrphanedClassCodes(): Promise<{
    totalMappings: number;
    deletedMappings: number;
    validMappings: number;
  }> {
    try {
      console.log('🧹 고아 클래스 코드 정리 시작...');
      
      // 모든 usedClassCodes 문서 조회
      const mappingsSnapshot = await getDocs(collection(db, COLLECTIONS.USED_CLASS_CODES));
      const totalMappings = mappingsSnapshot.docs.length;
      let deletedMappings = 0;
      
      console.log(`📊 총 ${totalMappings}개의 클래스 코드 매핑 발견`);
      
      for (const mappingDoc of mappingsSnapshot.docs) {
        const mapping = mappingDoc.data() as ClassCodeMapping;
        const classCode = mappingDoc.id;
        
        console.log(`🔍 검증 중: ${classCode} (User: ${mapping.userId}, Class: ${mapping.classId})`);
        
        try {
          // 실제 클래스 문서가 존재하는지 확인
          const classRef = doc(
            db, 
            COLLECTIONS.USERS, 
            mapping.userId, 
            COLLECTIONS.CLASSES, 
            mapping.classId
          );
          const classDoc = await getDoc(classRef);
          
          if (!classDoc.exists()) {
            // 클래스가 존재하지 않으면 매핑 삭제
            console.log(`❌ 클래스가 존재하지 않음. 매핑 삭제: ${classCode}`);
            await deleteDoc(mappingDoc.ref);
            deletedMappings++;
          } else {
            const classData = classDoc.data() as { classCode?: string };
            
            // 클래스 문서의 classCode와 매핑이 일치하는지 확인
            if (classData.classCode !== classCode) {
              console.log(`⚠️ 클래스 코드 불일치. 매핑 삭제: ${classCode} (클래스 실제 코드: ${classData.classCode})`);
              await deleteDoc(mappingDoc.ref);
              deletedMappings++;
            } else {
              console.log(`✅ 유효한 매핑: ${classCode}`);
            }
          }
        } catch (error) {
          // 사용자나 클래스에 접근할 수 없으면 매핑 삭제
          console.log(`❌ 접근 불가. 매핑 삭제: ${classCode}`, error);
          await deleteDoc(mappingDoc.ref);
          deletedMappings++;
        }
      }
      
      const result = {
        totalMappings,
        deletedMappings,
        validMappings: totalMappings - deletedMappings
      };
      
      console.log('✅ 고아 클래스 코드 정리 완료:', result);
      return result;
    } catch (error) {
      console.error('❌ 고아 클래스 코드 정리 실패:', error);
      throw error;
    }
  },

  // 클래스 코드 변경 (클래스 수정 시)
  async updateClassCode(
    oldClassCode: string,
    newClassCode: string,
    userId: string,
    classId: string,
    className: string
  ): Promise<void> {
    try {
      console.log('🔄 클래스 코드 변경 시작:', { oldClassCode, newClassCode });
      
      // 1. 새로운 클래스 코드 등록
      await this.registerClassCode(newClassCode, userId, classId, className);
      
      // 2. 기존 클래스 코드 삭제
      if (oldClassCode !== newClassCode) {
        await this.unregisterClassCode(oldClassCode);
      }
      
      console.log('✅ 클래스 코드 변경 완료:', { newClassCode });
    } catch (error) {
      console.error('❌ 클래스 코드 변경 실패:', error);
      throw error;
    }
  },

  // 멤버의 미확인/제출확인 과제 목록 가져오기 (V2: assignments/studentStatuses/subTasks 객체 듀얼 지원)
  async getStudentPendingAssignments(
    userId: string,
    classId: string,
    studentId: string,
    memberGroup?: string
  ): Promise<any[]> {
    try {
      console.log('📋 멤버 미확인 과제 조회 시작:', { userId, classId, studentId });

      // V2: studentStatuses는 키가 studentId, 값에 studentId 없을 수 있음 → entries로 studentId 보강
      const studentStatusesToArray = (raw: any): any[] => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        if (typeof raw === 'object') return Object.entries(raw).map(([sid, s]: [string, any]) => ({ studentId: s?.studentId ?? sid, ...s }));
        return [];
      };
      const toArray = (raw: any): any[] => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        if (typeof raw === 'object') return Object.values(raw);
        return [];
      };
      const assignmentsRawToArray = (raw: any): any[] => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw.map((a: any) => ({ ...a, studentStatuses: studentStatusesToArray(a.studentStatuses) }));
        if (typeof raw === 'object') return Object.entries(raw).map(([id, a]: [string, any]) => ({ assignmentId: id, ...a, studentStatuses: studentStatusesToArray(a?.studentStatuses) }));
        return [];
      };
      const subTasksRawToArray = (raw: any): any[] => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw.map((s: any) => ({ ...s, studentStatuses: studentStatusesToArray(s.studentStatuses) }));
        if (typeof raw === 'object') {
          const arr = Object.entries(raw).map(([id, s]: [string, any]) => ({ subTaskId: id, ...s, studentStatuses: studentStatusesToArray(s?.studentStatuses) }));
          arr.sort((a: any, b: any) => (a.subTaskOrder ?? 0) - (b.subTaskOrder ?? 0));
          return arr;
        }
        return [];
      };
      const projectAssignmentsRawToArray = (raw: any): any[] => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw.map((a: any) => ({ ...a, subTasks: subTasksRawToArray(a.subTasks) }));
        if (typeof raw === 'object') return Object.entries(raw).map(([id, a]: [string, any]) => ({ assignmentId: id, ...a, subTasks: subTasksRawToArray(a?.subTasks) }));
        return [];
      };
      
      const pendingAssignments: any[] = [];

      // 1. 일반과제 (GeneralAssignment-X) 조회
      const generalAssignmentStatuses = collection(
        db,
        COLLECTIONS.USERS,
        userId,
        COLLECTIONS.CLASSES,
        classId,
        withMemberGroup('generalAssignmentStatuses', memberGroup)
      );
      const generalSnapshot = await getDocs(generalAssignmentStatuses);
      
      generalSnapshot.docs.forEach(docSnapshot => {
        const data = docSnapshot.data();
        const assignments = assignmentsRawToArray(data.assignments);
        
        assignments.forEach((assignment: any) => {
          const studentStatuses = toArray(assignment.studentStatuses);
          const studentStatus = studentStatuses.find(
              (status: any) => status.studentId === studentId
            );
            
            if (studentStatus && (studentStatus.status === 'pending' || studentStatus.status === 'submitted')) {
              pendingAssignments.push({
                ...assignment,
                id: assignment.assignmentId || assignment.id,
                type: 'general',
                studentStatus: studentStatus,
                dueDate: assignment.dueDate?.toDate ? assignment.dueDate.toDate() : new Date(assignment.dueDate)
              });
          }
        });
      });

      // 2. 프로젝트과제 (ProjectAssignment-X) 조회
      const projectAssignmentStatuses = collection(
        db,
        COLLECTIONS.USERS,
        userId,
        COLLECTIONS.CLASSES,
        classId,
        withMemberGroup('projectAssignmentStatuses', memberGroup)
      );
      const projectSnapshot = await getDocs(projectAssignmentStatuses);
      
      projectSnapshot.docs.forEach(docSnapshot => {
        const data = docSnapshot.data();
        const assignments = projectAssignmentsRawToArray(data.assignments);
        
        assignments.forEach((assignment: any) => {
          const subTasks = subTasksRawToArray(assignment.subTasks);
          subTasks.forEach((subTask: any) => {
            const studentStatuses = toArray(subTask.studentStatuses);
            const studentStatus = studentStatuses.find(
                  (status: any) => status.studentId === studentId
                );
                
                if (studentStatus && (studentStatus.status === 'pending' || studentStatus.status === 'submitted')) {
              const projectColor = (assignment.color && String(assignment.color).trim()) ? assignment.color : '#FC5555';
                  pendingAssignments.push({
                    ...assignment,
                    id: assignment.assignmentId || assignment.id,
                    type: 'project',
                color: projectColor,
                    subTaskId: subTask.subTaskId,
                    subTaskTitle: subTask.subTaskTitle,
                    subTaskOrder: subTask.subTaskOrder,
                    basePoints: subTask.subTaskBasePoints,
                    studentStatus: studentStatus,
                    dueDate: assignment.dueDate?.toDate ? assignment.dueDate.toDate() : new Date(assignment.dueDate)
                  });
              }
            });
        });
      });

      // 3. 루틴과제는 멤버 개별 '나의 상태 확인' 페이지에서 제외
      // (루틴 과제는 별도로 관리되므로 조회하지 않음)

      // 마감일 기준 정렬 (가까운 순)
      pendingAssignments.sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());

      console.log(`✅ 멤버 미확인 과제 ${pendingAssignments.length}개 조회 완료`);
      return pendingAssignments;

    } catch (error) {
      console.error('❌ 멤버 미확인 과제 조회 실패:', error);
      return [];
    }
  },

  /** 오늘 날짜를 YYYY-MM-DD로 변환 (로컬) */
  _dateToDateId(d: Date): string {
    return d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0');
  },

  /** 멤버 개별 접근: 오늘의 루틴 과제 목록 (해당 학생이 할당된 것만, 상태 포함) */
  async getStudentTodayRoutineAssignments(
    userId: string,
    classId: string,
    studentId: string,
    memberGroup?: string
  ): Promise<Array<{ id: string; routineAssignmentId: string; dateId: string; title: string; status: string; basePoints: number }>> {
    try {
      const { routineAssignmentService } = await import('./routineAssignmentService');
      const { RoutineAssignmentConsolidatedService } = await import('./routineAssignmentConsolidatedService');

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dateId = this._dateToDateId(today);
      const dayOfWeek = today.getDay();

      const routines = await routineAssignmentService.loadRoutineAssignments(userId, classId, memberGroup);
      const consolidated = new RoutineAssignmentConsolidatedService(userId, classId, memberGroup);
      const result: Array<{ id: string; routineAssignmentId: string; dateId: string; title: string; status: string; basePoints: number }> = [];

      for (const r of routines) {
        if (!r.isActive || !r.assignedStudentIds?.length) continue;
        if (!r.assignedStudentIds.includes(studentId)) continue;
        if (!r.routineDays?.length || !r.routineDays.includes(dayOfWeek)) continue;

        const start = r.startDate ? new Date(r.startDate) : new Date(today.getFullYear() - 1, 0, 1);
        const due = r.dueDate ? new Date(r.dueDate) : new Date(today.getFullYear() + 1, 11, 31);
        start.setHours(0, 0, 0, 0);
        due.setHours(23, 59, 59, 999);
        if (today < start || today > due) continue;

        const { dateStatuses } = await consolidated.loadRoutineAssignmentWithStatuses(r.id);
        const dateAssignment = dateStatuses?.find((da: any) => da.date === dateId);
        const studentStatus = dateAssignment?.studentStatuses?.find((s: any) => s.studentId === studentId);
        const status = studentStatus?.status === 'confirmed' ? 'confirmed' : 'pending';
        const basePoints = r.basePoints ?? 10;

        result.push({
          id: `${r.id}-${dateId}`,
          routineAssignmentId: r.id,
          dateId,
          title: r.title,
          status,
          basePoints,
        });
      }

      return result;
    } catch (e) {
      console.error('❌ 오늘 루틴 과제 조회 실패:', e);
      return [];
    }
  },

  /** 멤버 개별 접근: 오늘 루틴 과제 자기 확인 토글 (pending ↔ confirmed) */
  async toggleRoutineAssignmentSelfCheck(
    userId: string,
    classId: string,
    studentId: string,
    routineId: string,
    dateId: string,
    memberGroup?: string
  ): Promise<{ success: boolean; status: string; error?: string }> {
    try {
      const { routineAssignmentService } = await import('./routineAssignmentService');
      const { RoutineAssignmentConsolidatedService } = await import('./routineAssignmentConsolidatedService');

      const routines = await routineAssignmentService.loadRoutineAssignments(userId, classId, memberGroup);
      const routine = routines.find((r) => r.id === routineId);
      if (!routine) return { success: false, status: 'pending', error: '루틴 과제를 찾을 수 없습니다.' };

      const consolidated = new RoutineAssignmentConsolidatedService(userId, classId, memberGroup);
      const { dateStatuses } = await consolidated.loadRoutineAssignmentWithStatuses(routineId);
      const dateAssignment = dateStatuses?.find((da: any) => da.date === dateId);
      const studentStatus = dateAssignment?.studentStatuses?.find((s: any) => s.studentId === studentId);
      const current = studentStatus?.status === 'confirmed' ? 'confirmed' : 'pending';
      const next = current === 'confirmed' ? 'pending' : 'confirmed';
      const points = next === 'confirmed' ? (routine.basePoints ?? 10) : 0;

      await consolidated.updateStudentStatus(routineId, dateId, studentId, next as 'pending' | 'confirmed', points, '');
      return { success: true, status: next };
    } catch (e) {
      console.error('❌ 루틴 과제 자기 확인 토글 실패:', e);
      return { success: false, status: 'pending', error: (e as Error).message };
    }
  },

  // 멤버가 과제 제출하기
  async submitAssignment(
    userId: string,
    classId: string,
    studentId: string,
    assignmentId: string,
    assignmentType: 'general' | 'project' | 'routine',
    submissionLink: string,
    submissionMessage: string,
    dateId?: string, // 루틴 과제일 경우 필요
    subTaskId?: string, // 프로젝트 과제일 경우 필요
    completionCode?: string, // 일반 과제 완료 코드 (보상 지급용)
    memberGroup?: string,
    submissionImage?: {
      path: string;
      updatedAt: string;
      sizeBytes: number;
    },
    options?: { clearSubmissionImage?: boolean }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('📤 과제 제출 시작:', { 
        userId, classId, studentId, assignmentId, assignmentType 
      });

      // 루틴 과제는 실제 구조(routineAssignmentStatuses/{routineId}/DateAssignment/{dateDocId})를 사용
      if (assignmentType === 'routine') {
        if (!dateId) {
          return { success: false, error: '루틴 과제 제출에는 날짜 ID가 필요합니다.' };
        }
        
        try {
          // routineAssignmentStatuses/{routineId}/DateAssignment 컬렉션에서 해당 날짜 문서 찾기
          const routineCollection = collection(
            db,
            COLLECTIONS.USERS,
            userId,
            COLLECTIONS.CLASSES,
            classId,
            withMemberGroup('routineAssignmentStatuses', memberGroup),
            assignmentId,
            'DateAssignment'
          );
          
          const dateSnapshot = await getDocs(routineCollection);
          let updated = false;
          
          for (const dateDoc of dateSnapshot.docs) {
            const dateData = dateDoc.data();
            const dateAssignments = Array.isArray(dateData.dateAssignments) ? dateData.dateAssignments : [];
            const hasTargetDate = dateAssignments.some((da: any) => da.dateId === dateId);
            if (!hasTargetDate) continue;

            const txResult = await runTransaction(db, async (tx) => {
              const freshSnap = await tx.get(dateDoc.ref);
              if (!freshSnap.exists()) {
                return { updated: false };
              }

              const freshData = freshSnap.data();
              const freshDateAssignments = Array.isArray(freshData.dateAssignments) ? freshData.dateAssignments : [];
              let modified = false;

              const updatedDateAssignments = freshDateAssignments.map((da: any) => {
                if (da.dateId !== dateId) return da;

                const studentStatuses = Array.isArray(da.studentStatuses) ? [...da.studentStatuses] : [];
              const studentIndex = studentStatuses.findIndex((ss: any) => ss.studentId === studentId);
              
              if (studentIndex >= 0) {
                const updatedStatus: any = {
                  ...studentStatuses[studentIndex],
                  status: 'submitted',
                  submittedAt: new Date(),
                  updatedAt: new Date()
                };
                if (submissionLink && submissionLink.trim()) {
                  updatedStatus.submissionLink = submissionLink;
                }
                if (submissionMessage && submissionMessage.trim()) {
                  updatedStatus.submissionMessage = submissionMessage;
                }
                studentStatuses[studentIndex] = updatedStatus;
              } else {
                const newStatus: any = {
                  studentId,
                  status: 'submitted',
                  submittedAt: new Date(),
                  updatedAt: new Date()
                };
                if (submissionLink && submissionLink.trim()) {
                  newStatus.submissionLink = submissionLink;
                }
                if (submissionMessage && submissionMessage.trim()) {
                  newStatus.submissionMessage = submissionMessage;
                }
                studentStatuses.push(newStatus);
              }
              
                modified = true;
                return { ...da, studentStatuses };
              });

              if (!modified) {
                return { updated: false };
              }

              tx.update(dateDoc.ref, {
                dateAssignments: updatedDateAssignments,
                lastUpdated: new Date()
              });
              
              return { updated: true };
            });

            if (txResult.updated) {
              updated = true;
              break;
            }
          }
          
          if (!updated) {
            return { success: false, error: '해당 날짜의 루틴 과제를 찾을 수 없습니다.' };
          }
          
          console.log('✅ 루틴 과제 제출 완료');
          return { success: true };
        } catch (error) {
          console.error('❌ 루틴 과제 제출 실패:', error);
          return { success: false, error: '루틴 과제 제출 중 오류가 발생했습니다.' };
        }
      }

      // 일반/프로젝트 과제: 통합 서비스 사용 (V2 객체 구조 지원)
      if (assignmentType === 'general') {
        const { GeneralAssignmentConsolidatedService } = await import('./generalAssignmentConsolidatedService');
        const generalService = new GeneralAssignmentConsolidatedService(userId, classId, memberGroup);
        const assignment = await generalService.loadGeneralAssignment(assignmentId);
        if (!assignment) {
          console.log('❌ 과제를 찾을 수 없습니다.');
          return { success: false, error: '과제를 찾을 수 없습니다.' };
        }
                const hasCompletionCode = completionCode && assignment.rewardEnabled && assignment.rewardCode;
        const isCodeValid = !!hasCompletionCode && completionCode!.trim() === assignment.rewardCode!.trim();
        const status = isCodeValid ? 'confirmed' : 'submitted';
        const points = isCodeValid ? (assignment.basePoints ?? 0) : 0;
        await generalService.updateStudentStatus(
          assignmentId,
          studentId,
          status as 'pending' | 'submitted' | 'confirmed' | 'delayed' | 'exempted',
          points,
          '',
          submissionLink,
          submissionMessage,
          new Date(),
          submissionImage?.path,
          submissionImage?.updatedAt,
          submissionImage?.sizeBytes,
          options?.clearSubmissionImage
        );
        console.log('✅ 과제 제출 완료');
        return { success: true };
      }

      if (assignmentType === 'project') {
        if (!subTaskId) {
          return { success: false, error: '프로젝트 과제 제출에는 하위 과제 ID가 필요합니다.' };
        }
        const { ProjectAssignmentConsolidatedService } = await import('./projectAssignmentConsolidatedService');
        const projectService = new ProjectAssignmentConsolidatedService(userId, classId, memberGroup);
        const projectStatusUpdate: {
          studentId: string;
          status: 'submitted';
          points: number;
          submissionLink?: string;
          submissionMessage?: string;
          submittedAt: Date;
          submissionImagePath?: string;
          submissionImageUpdatedAt?: string;
          submissionImageSizeBytes?: number;
        } = {
          studentId,
          status: 'submitted',
          points: 0,
          submissionLink: submissionLink || undefined,
          submissionMessage: submissionMessage || undefined,
          submittedAt: new Date()
        };
        if (submissionImage) {
          projectStatusUpdate.submissionImagePath = submissionImage.path;
          projectStatusUpdate.submissionImageUpdatedAt = submissionImage.updatedAt;
          projectStatusUpdate.submissionImageSizeBytes = submissionImage.sizeBytes;
        } else if (options?.clearSubmissionImage) {
          projectStatusUpdate.submissionImagePath = undefined;
          projectStatusUpdate.submissionImageUpdatedAt = undefined;
          projectStatusUpdate.submissionImageSizeBytes = undefined;
        }
        await projectService.batchUpdateSubTaskStudentStatuses(
          assignmentId,
          subTaskId,
          [projectStatusUpdate]
        );
        console.log('✅ 과제 제출 완료');
        return { success: true };
      }

      return { success: false, error: '잘못된 과제 타입입니다.' };

    } catch (error) {
      console.error('❌ 과제 제출 실패:', error);
      return { success: false, error: '과제 제출에 실패했습니다.' };
    }
  },

  /**
   * 멤버의 보상 알람 제거
   */
  async removeStudentNotification(
    userId: string,
    classId: string,
    studentId: string,
    notificationId: string,
    memberGroup?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🔔 알람 제거 시작:', { studentId, notificationId });

      const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
      const partitionService = new ClassMembersPartitionService(userId, classId, memberGroup);

      await partitionService.removeStudentPendingNotificationAtomic(studentId, notificationId);
      console.log(`✅ 알람 제거 완료: 멤버 ${studentId}, 알람 ${notificationId}`);
      
      return { success: true };
    } catch (error) {
      console.error('❌ 알람 제거 실패:', error);
      return { success: false, error: '알람 제거에 실패했습니다.' };
    }
  },

  async getClassLocationByCode(classCode: string): Promise<{ userId: string; classId: string } | null> {
    if (classLocationCache.has(classCode)) {
      return classLocationCache.get(classCode) || null;
    }
    try {
      console.log('🔍 클래스 위치 조회 시작:', classCode);
      const callable = httpsCallable<{ classCode: string }, { userId: string | null; classId: string | null }>(
        functions,
        'getClassLocationByCode'
      );
      const result = await callable({ classCode });
      const data = result.data;
      if (data?.userId && data?.classId) {
        const location = { userId: data.userId, classId: data.classId };
        classLocationCache.set(classCode, location);
        return location;
      }
      classLocationCache.set(classCode, null);
      return null;
    } catch (error: any) {
      console.error('❌ 클래스 위치 조회 실패:', { classCode, error: error?.message });
      classLocationCache.set(classCode, null);
      return null;
    }
  },

  /**
   * ClassCodeMapping 컬렉션을 usedClassCodes로 마이그레이션
   */
  async migrateClassCodeMappingToUsedClassCodes(): Promise<{
    total: number;
    migrated: number;
    skipped: number;
    errors: number;
  }> {
    try {
      console.log('🚀 ClassCodeMapping → usedClassCodes 마이그레이션 시작');
      
      // ClassCodeMapping 컬렉션의 모든 문서 조회
      const classCodeMappingRef = collection(db, 'ClassCodeMapping');
      const snapshot = await getDocs(classCodeMappingRef);
      
      const result = {
        total: snapshot.size,
        migrated: 0,
        skipped: 0,
        errors: 0
      };
      
      console.log(`📊 ClassCodeMapping 문서 수: ${result.total}`);
      
      if (snapshot.empty) {
        console.log('ℹ️ 마이그레이션할 문서가 없습니다.');
        return result;
      }
      
      // 각 문서를 usedClassCodes로 복사 또는 병합
      for (const docSnap of snapshot.docs) {
        const classCode = docSnap.id;
        const data = docSnap.data();
        
        try {
          // usedClassCodes에 이미 존재하는지 확인
          const usedCodeRef = doc(db, COLLECTIONS.USED_CLASS_CODES, classCode);
          const existingDoc = await getDoc(usedCodeRef);
          
          const newData = {
            userId: data.userId,
            classId: data.classId,
            className: data.className || '',
            createdAt: data.createdAt || new Date(),
            isActive: data.isActive !== undefined ? data.isActive : true
          };
          
          if (existingDoc.exists()) {
            // 이미 존재하면 누락된 필드만 병합
            const existingData = existingDoc.data();
            const fieldsToUpdate: any = {};
            
            // className이 없거나 빈 문자열이면 업데이트
            if (!existingData.className && newData.className) {
              fieldsToUpdate.className = newData.className;
            }
            // isActive가 없으면 업데이트
            if (existingData.isActive === undefined) {
              fieldsToUpdate.isActive = newData.isActive;
            }
            
            if (Object.keys(fieldsToUpdate).length > 0) {
              await setDoc(usedCodeRef, fieldsToUpdate, { merge: true });
              console.log(`🔄 ${classCode}: 필드 업데이트됨 - ${Object.keys(fieldsToUpdate).join(', ')}`);
              result.migrated++;
            } else {
              console.log(`⏭️ ${classCode}: 이미 usedClassCodes에 완전히 존재함 - 스킵`);
              result.skipped++;
            }
            continue;
          }
          
          // usedClassCodes에 문서 생성
          await setDoc(usedCodeRef, newData);
          
          console.log(`✅ ${classCode}: 마이그레이션 완료`);
          result.migrated++;
          
        } catch (error: any) {
          console.error(`❌ ${classCode}: 마이그레이션 실패 -`, error.message);
          result.errors++;
        }
      }
      
      console.log('📊 마이그레이션 결과:', result);
      return result;
      
    } catch (error) {
      console.error('❌ 마이그레이션 실패:', error);
      throw error;
    }
  },

  /**
   * 클래스 코드로 개별 접근 페이지 설정 조회 (인증 없이 Callable 호출)
   */
  async getIndividualAccessSettings(classCode: string): Promise<{
    routineSectionEnabled: boolean;
    storeIndividualPurchaseEnabled: boolean;
  }> {
    try {
      const callable = httpsCallable<
        { classCode: string },
        { routineSectionEnabled: boolean; storeIndividualPurchaseEnabled: boolean }
      >(functions, 'getIndividualAccessSettingsByCode');
      const result = await callable({ classCode });
      const data = result.data;
      return {
        routineSectionEnabled: data?.routineSectionEnabled !== undefined ? data.routineSectionEnabled : true,
        storeIndividualPurchaseEnabled: data?.storeIndividualPurchaseEnabled !== undefined ? data.storeIndividualPurchaseEnabled : true,
      };
    } catch (error) {
      console.error('❌ 개별 접근 설정 조회 실패:', error);
      return { routineSectionEnabled: true, storeIndividualPurchaseEnabled: true };
    }
  },

  async uploadAssignmentSubmissionImage(params: {
    classCode: string;
    studentNumber: string;
    studentName: string;
    accessCode?: string;
    memberGroup?: string;
    memberId?: string;
    assignmentType: 'general' | 'project';
    assignmentId: string;
    subTaskId?: string;
    imageBase64: string;
    mimeType: string;
  }): Promise<{
    success: boolean;
    submissionImagePath?: string;
    submissionImageUpdatedAt?: string;
    submissionImageSizeBytes?: number;
    error?: string;
  }> {
    try {
      const callable = httpsCallable<
        typeof params,
        {
          success: boolean;
          submissionImagePath?: string;
          submissionImageUpdatedAt?: string;
          submissionImageSizeBytes?: number;
          error?: string;
        }
      >(functions, 'uploadAssignmentSubmissionImage');
      const result = await callable(params);
      return result.data;
    } catch (error: any) {
      console.error('❌ 과제 제출 이미지 업로드 실패:', error);
      return {
        success: false,
        error: error?.message || '이미지 업로드에 실패했습니다.'
      };
    }
  },

  async deleteOwnAssignmentSubmissionImage(params: {
    classCode: string;
    studentNumber: string;
    studentName: string;
    accessCode?: string;
    memberGroup?: string;
    memberId?: string;
    submissionImagePath: string;
  }): Promise<{ success: boolean; error?: string }> {
    try {
      const callable = httpsCallable<typeof params, { success: boolean; error?: string }>(
        functions,
        'deleteOwnAssignmentSubmissionImage'
      );
      const result = await callable(params);
      return result.data;
    } catch (error: any) {
      console.error('❌ 제출 이미지 삭제 실패:', error);
      return { success: false, error: error?.message };
    }
  },

  async getOwnSubmissionImageUrl(params: {
    classCode: string;
    studentNumber: string;
    studentName: string;
    accessCode?: string;
    memberGroup?: string;
    memberId?: string;
    submissionImagePath: string;
  }): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      const callable = httpsCallable<
        typeof params,
        { success: boolean; url?: string; error?: string }
      >(functions, 'getOwnSubmissionImageUrl');
      const result = await callable(params);
      return result.data;
    } catch (error: any) {
      console.error('❌ 제출 이미지 URL 조회 실패:', error);
      return { success: false, error: error?.message };
    }
  },

  // 클래스 코드로 코디 정보 가져오기 (인증 없이 Callable 호출)
  async getCodiInfoByClassCode(classCode: string): Promise<{ userId: string; userType?: string } | null> {
    try {
      const callable = httpsCallable<
        { classCode: string },
        { userId: string | null; userType?: string }
      >(functions, 'getCodiInfoByClassCode');
      const result = await callable({ classCode });
      const data = result.data;
      if (!data?.userId) return null;
      return { userId: data.userId, userType: data.userType };
    } catch (error) {
      console.error('❌ 코디 정보 조회 실패:', error);
      return null;
    }
  }
};

export type { StudentAccessResult, StudentSummary, ClassCodeMapping, ClassMember };

